import { useNavigate } from 'react-router-dom';
import { Flame, Sun, Snowflake, ThermometerSnowflake, HelpCircle } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { TemperatureCounts, Temperature } from '../../types';

interface TemperatureChartProps {
  data: TemperatureCounts;
  isLoading?: boolean;
  scope?: 'all' | 'mine';
}

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const TEMPERATURE_CONFIG: Record<Temperature | 'unset', {
  label: string;
  Icon: IconComponent;
  iconColor: string;
  color: string;
  bgColor: string;
  textColor: string;
}> = {
  HOT: {
    label: 'Hot',
    Icon: Flame,
    iconColor: 'text-red-500 dark:text-red-400',
    color: 'bg-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-700 dark:text-red-400',
  },
  WARM: {
    label: 'Warm',
    Icon: Sun,
    iconColor: 'text-amber-500 dark:text-amber-400',
    color: 'bg-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    textColor: 'text-amber-700 dark:text-amber-400',
  },
  COLD: {
    label: 'Cold',
    Icon: Snowflake,
    iconColor: 'text-sky-500 dark:text-sky-400',
    color: 'bg-sky-500',
    bgColor: 'bg-sky-100 dark:bg-sky-900/30',
    textColor: 'text-sky-700 dark:text-sky-400',
  },
  FREEZING: {
    label: 'Freezing',
    Icon: ThermometerSnowflake,
    iconColor: 'text-slate-500 dark:text-slate-400',
    color: 'bg-slate-500',
    bgColor: 'bg-slate-100 dark:bg-slate-800/40',
    textColor: 'text-slate-700 dark:text-slate-300',
  },
  unset: {
    label: 'Not Set',
    Icon: HelpCircle,
    iconColor: 'text-gray-400 dark:text-gray-500',
    color: 'bg-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    textColor: 'text-gray-600 dark:text-gray-400',
  },
};

const TEMPERATURE_ORDER: (Temperature | 'unset')[] = ['HOT', 'WARM', 'COLD', 'FREEZING', 'unset'];

export const TemperatureChart = ({ data, isLoading, scope = 'all' }: TemperatureChartProps) => {
  const navigate = useNavigate();

  const totalWithTemp = data.HOT + data.WARM + data.COLD + data.FREEZING;
  const total = totalWithTemp + data.unset;

  const handleTemperatureClick = (temp: Temperature | 'unset') => {
    if (temp === 'unset') {
      navigate(`/people?scope=${scope}`);
    } else {
      navigate(`/people?temperature=${temp}&scope=${scope}`);
    }
  };

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Temperature</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">{total.toLocaleString()} total</span>
      </div>

      {total === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No temperature data yet</p>
          <button
            onClick={() => navigate('/people')}
            className="mt-2 text-sm text-blast-600 hover:text-blast-700"
          >
            Add your first contact →
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {TEMPERATURE_ORDER.map((temp) => {
              const config = TEMPERATURE_CONFIG[temp];
              const { Icon } = config;
              const count = data[temp];
              const percentage = total > 0 ? (count / total) * 100 : 0;

              return (
                <button
                  key={temp}
                  onClick={() => handleTemperatureClick(temp)}
                  className={`${config.bgColor} rounded-lg p-4 text-center transition-all hover:ring-2 hover:ring-offset-1 hover:ring-blast-300 hover:shadow-md`}
                >
                  <Icon className={`h-7 w-7 mx-auto mb-1 ${config.iconColor}`} />
                  <div className={`text-xl font-bold ${config.textColor}`}>
                    {count.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                    {config.label}
                  </div>
                  {percentage > 0 && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {percentage.toFixed(0)}%
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {totalWithTemp > 0 && (
            <div className="mt-4">
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex">
                {(['HOT', 'WARM', 'COLD', 'FREEZING'] as Temperature[]).map((temp) => {
                  const config = TEMPERATURE_CONFIG[temp];
                  const count = data[temp];
                  const percentage = totalWithTemp > 0 ? (count / totalWithTemp) * 100 : 0;

                  if (percentage === 0) return null;

                  return (
                    <div
                      key={temp}
                      className={`${config.color} transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                      title={`${config.label}: ${count} (${percentage.toFixed(0)}%)`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-center">
            Click any temperature to view people
          </p>
        </>
      )}
    </div>
  );
};
